import { useContext, useMemo, useState } from "react";
import { AppContext, AppStateProps } from "../../context";
import { ClientList } from "../shared/ClientList";
import { BoxFull, Button, Card } from "iuvity-ui-components";
import { SettingsUsed } from "../shared/SettingsUsed";
import { BaseRoleList } from "../shared/BaseRoleList";
import { LastChangesTable } from "../shared/LastChangesTable";
import { useTenantBranding } from "../../hooks/useTenantBranding";
import { DataBranding } from "../../interfaces";
import { useTenants } from "../../hooks/useTenants";
import { useQuickLinksConsole } from "../../hooks/useQuickLinksConsole";
import { useUserRole } from "../../hooks/useUserRole";
import { useAudit } from "../../hooks/useAudit";
import { CustomDialog, DialogProps } from "../dialog/CustomDialog";
import { useApplications } from "../../hooks/useApplications";
import { auth } from "../../services/iam/auth";

export const AppsMenu = ({ data, apps, setInfoApps }: any) => {
  const {
    t,
    appState: { language, isMainTenant, tenantId, tenantIdListsNavigations, isLoading, adminRoleId },
    setAppState,
  } = useContext(AppContext);

  const { getBrandingInfo } = useTenantBranding("DATA");
  const { data: dataQuickLinks } = useQuickLinksConsole("SELECT_TENANT_DASHBOARD");
  const { data: dataBaseRoles } = useUserRole("DATA");
  const { data: dataAudit } = useAudit("DATA");
  const { getInfoChildrenTenants } = useTenants("CHILDREN");
  const [infoData, setInfoData] = useState(data[0].childrenTenants);
  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);
  const { getInfoApplicationsByTenant } = useApplications("APPS");
  const showAuditWidget = !!dataBaseRoles && !!dataBaseRoles[0]?.rolePermissions?.find(
    (rol: any) => (rol?.functionality?.code === "COD_AREP" && rol.permission.privilegeId === "READ"));

  const lastChangesData = [
    {
        eventId: "511692557629181",
        functionality: "AUTHENTICATION",
        activity: "USER_PWD_VALIDATION",
        userId: null,
        email: "emaldonado@iuvity.com",
        creationDate: "2024-04-24T14:49:44.366Z",
        segment: null,
        tenantId: null,
        environment: null,
        tenant: null,
        role: null,
        status: "SUCCESS"
    },
    {
        eventId: "690922297160167",
        functionality: "AUTHENTICATION",
        activity: "CAPTCHA_VALIDATION",
        userId: null,
        email: "emaldonado@iuvity.com",
        creationDate: "2024-04-24T14:49:43.839Z",
        segment: null,
        tenantId: null,
        environment: null,
        tenant: null,
        role: null,
        status: "SUCCESS"
    },
    {
        eventId: "408624439859993",
        functionality: "AUTHENTICATION",
        activity: "USER_PWD_VALIDATION",
        userId: null,
        email: "jlopez@iuvity.com",
        creationDate: "2024-04-23T21:40:05.958Z",
        segment: null,
        tenantId: null,
        environment: null,
        tenant: null,
        role: null,
        status: "SUCCESS"
    },
    {
        eventId: "798373709363760",
        functionality: "AUTHENTICATION",
        activity: "CAPTCHA_VALIDATION",
        userId: null,
        email: "jlopez@iuvity.com",
        creationDate: "2024-04-23T21:40:05.596Z",
        segment: null,
        tenantId: null,
        environment: null,
        tenant: null,
        role: null,
        status: "SUCCESS"
    },
  ];

  const onSelectTenantId = () => {
    setAppState((prev) => ({
      ...prev,
      selectedTenantId: tenantId
    }));
  }

  const onSelectClientId = async (tenantid: any, tenantname?: string) => {
    setInfoApps([]);
    setAppState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    await auth.selectTenantAction(tenantid, adminRoleId);
    const tenant = await Promise.resolve(getInfoChildrenTenants(tenantid));

    setAppState((prev:AppStateProps)=>({
      ...prev,
      childrenTenants: tenant[0].childrenTenants ?? [],
      aliasParentTenant: tenant[0].alias ?? '',
    }));

    if (!!tenant && tenant[0]?.childrenTenants?.length > 0) {
      const dataBranding: DataBranding = await Promise.resolve(getBrandingInfo(tenantid));
      const res = await Promise.resolve(getInfoApplicationsByTenant(tenantid));
      setInfoApps(res);
      if (dataBranding) {
        setAppState((prev) => ({
          ...prev,
          dataBrandingObject: dataBranding,
          tenantParentName: !!tenantname ? tenantname : tenant[0]?.name,
          tenantId: tenantid
        }));
      }
      setInfoData(tenant[0].childrenTenants);
      setAppState((prev) => ({
        ...prev,
        isMainTenant: tenant[0].maintenant,
      }));
    } else {
      const dataBranding: DataBranding = await Promise.resolve(getBrandingInfo(tenantid));
      await Promise.resolve(getInfoApplicationsByTenant(tenantid));
      if (dataBranding) {
        setAppState((prev) => ({
          ...prev,
          selectedTenantId: tenantid,
          dataBrandingObject: dataBranding,
          tenantParentName: !!tenantname ? tenantname : tenant[0]?.name,
          tenantIdListsNavigations: [...new Set([...tenantIdListsNavigations, tenantid])],
        }));
      }
    }

    setAppState((prev) => ({
      ...prev,
      isLoading: false,
    }));

    window.scrollTo(0, 0);
  };

  const onReturn = async () => {
    const arrayNavigation: any = tenantIdListsNavigations.filter(item => item !== tenantIdListsNavigations[tenantIdListsNavigations.length - 1]);
    setAppState((prev) => ({
      ...prev,
      tenantId: arrayNavigation.slice(-1)[0],
      tenantIdListsNavigations: arrayNavigation.slice(0, -1),
      selectedApp: null
    }));
    onSelectClientId(arrayNavigation.slice(-1)[0]);
  }

  const handleModalProps = (actionType: "action" | "cancel") => {
    switch (actionType) {
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("AppSelector.cancelModalTitle"),
          content: t("AppSelector.cancelModalContent"),
          onConfirm: () => onReturn(),
        }));
      default:
        return;
    }
  };

  const columnsLastChanges = useMemo(() => {
    if (language) {
      return [
        {
          field: "creationDate",
          header: t("Dashboard.updatedAtLabel"),
        },
        {
          field: "functionality",
          header: t("Dashboard.functionalityLabel"),
        },
        {
          field: "userEmail",
          header: t("Dashboard.userLabel"),
        },
        {
          field: "activity",
          header: t("Dashboard.activityLabel"),
        },
      ];
    } else {
      return [];
    }
  }, [t, language]);

  return (
    <div className="dashboard-external-container">
      {tenantIdListsNavigations?.length !== 1 && <BoxFull
        className="flex px-0 pb-0 py-5"
        variant="tertiary"
      >
        <div className="flex" style={{ position: "relative", left: "-1rem" }}>
          <Button
            variant="p-button-link"
            icon="iu iu-icon-left"
            label={t("AppSelector.selectApplicationClientTitle")}
            onClick={() => handleModalProps("cancel")}
          />
        </div>
      </BoxFull>}

      <BoxFull className="p-0" style={{ backgroundColor: "transparent" }}>
        <div className={`inputlabel ${tenantIdListsNavigations?.length !== 1 ? "client-title pt-0" : "client-title"}`}>
          {t("AppSelector.clientSelectorTitle")}
        </div>

        <div className="my-4 client-subtitle">
          {t("Dashboard.clientSelectorDescription")}
        </div>

      </BoxFull>

      {!isLoading
        ?
          <ClientList
            data={infoData}
            isActions={true}
            carrousel={true}
            onSelectClientId={onSelectClientId}
            apps={apps}
            onSelectTenantId={onSelectTenantId}
          />
        :
          <div className="skeleton-clients-container">
            <div className="card-actions">
            <Card className="actions"></Card>
            </div>
            <div className="skeleton">
                {[1,2,3,4].map(() => (
                  <Card className="card-skeleton"></Card>
                ))}
            </div>
          </div>
      }

      {!isLoading
        ?
          <BoxFull className="my-3 p-0" style={{ backgroundColor: "transparent" }}>
            <div className={`${isMainTenant && dataQuickLinks?.length > 0 ? "config-grid-3" : isMainTenant && dataQuickLinks?.length === 0 ? "config-grid-2" : "config-grid-1"}`}>
                {dataQuickLinks?.length > 0 && <SettingsUsed
                  title={t("Dashboard.mostUsedConfigurationsTitle")}
                  data={dataQuickLinks}
                />}
                {isMainTenant && <BaseRoleList
                  title={t("Dashboard.baseRolesTitle")}
                  data={dataBaseRoles}
                />}
                {dataAudit?.length > 0 && showAuditWidget && <LastChangesTable
                  title={t("Dashboard.latestChangesTitle")}
                  data={dataAudit}
                  columns={columnsLastChanges}
                />}
              </div>
          </BoxFull>
        :
          <div className="skeleton-widgets-container">
            <div className="card-1"></div>
            <div className="card-2"></div>
          </div>
      }

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </div>
  );
};
